import { useEnum } from "@/api/services/global/enum";
import { InnerBox } from "@/components/elements/InnerBox";
import { TalentInfoItem } from "@/features/talent/components/talent-detail/TalentInfoItem";
import { CollapsibleSectionHeader, useCollapsible } from "@/features/talent/components/talent-common/CollapsibleSectionHeader";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { divider } from "@/theme/colors";
import { FCC } from "@/types/common";
import { TALENT_LOCATION_OPTIONS } from "@/types/enum";
import { findLabelByValue } from "@/utils/object";
import { Stack, Grid, Collapse } from "@mui/material";

type TSharedTalentBasicInfoProps = {
  defaultValues?: Partial<TTalentForm>;
};

/** Invisible placeholder — truthy so TalentInfoItem renders blank instead of "-" */
const BLANK = " ";

/**
 * Basic info section for shared talent page (masked view).
 * Shows all field labels but PII values are blank (not "-").
 * Non-PII fields (nationality, gender, location, religion, prefecture+city) display normally.
 */
export const SharedTalentBasicInfo: FCC<TSharedTalentBasicInfoProps> = ({ defaultValues }) => {
  const { data: genderOptions } = useEnum("EGender");
  const { expanded, toggle } = useCollapsible();

  return (
    <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
      <CollapsibleSectionHeader title="基本情報" expanded={expanded} onToggle={toggle} />

      <Collapse in={expanded}>
        <Stack mt={3}>
          <Grid container spacing={3} alignItems="stretch" sx={{ "& > .MuiGrid-item > *:first-child": { height: "100%" }, "& > .MuiGrid-item > *:first-child > .MuiDivider-root:last-child": { mt: "auto" } }}>
            <Grid item xs={6}>
              <TalentInfoItem label="氏名" value={defaultValues?.fullName || BLANK} />
            </Grid>
            <Grid item xs={6}>
              <TalentInfoItem label="カナ表記" value={defaultValues?.fullNameKana || BLANK} />
            </Grid>

            <Grid item xs={6}>
              <TalentInfoItem label="呼び名" value={BLANK} />
            </Grid>
            <Grid item xs={6}>
              <TalentInfoItem label="生年月日" value={BLANK} />
            </Grid>

            <Grid item xs={6}>
              <TalentInfoItem label="人材管理番号" value={defaultValues?.talentNumber || BLANK} />
            </Grid>

            <Grid item xs={6}>
              <TalentInfoItem label="国籍" value={defaultValues?.nationality?.nationalityName ?? ""} />
            </Grid>
            <Grid item xs={6}>
              <TalentInfoItem label="性別" value={findLabelByValue(genderOptions, defaultValues?.gender)} emptyText="" />
            </Grid>

            <Grid item xs={6}>
              <TalentInfoItem label="電話番号" value={BLANK} />
            </Grid>
            <Grid item xs={6}>
              <TalentInfoItem label="メール" value={BLANK} />
            </Grid>

            <Grid item xs={6}>
              <TalentInfoItem label="その他の連絡手段・SNS" value={BLANK} />
            </Grid>
            <Grid item xs={6}>
              <TalentInfoItem label="その他の連絡先ID等" value={BLANK} />
            </Grid>

            <Grid item xs={6}>
              <TalentInfoItem label="居住地" value={findLabelByValue(TALENT_LOCATION_OPTIONS, defaultValues?.location)} />
            </Grid>
            <Grid item xs={6}>
              <TalentInfoItem label="宗教" value={defaultValues?.religion} />
            </Grid>

            <Grid item xs={12}>
              <TalentInfoItem
                label="現住所"
                value={
                  defaultValues?.prefecture || defaultValues?.city
                    ? `${defaultValues.prefecture ?? ""}${defaultValues.city ?? ""}`.trim()
                    : BLANK
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TalentInfoItem label="本国における居住地" value={defaultValues?.country || BLANK} isShowDivider={false} />
            </Grid>
          </Grid>
        </Stack>
      </Collapse>
    </InnerBox>
  );
};
